import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { tap } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {

  private apiUrl = '/api/swirl';
  private tokenKey = 'swirl-token';
  private chatStatusKey = 'swirl-chat-status';
  private AIProviderStatusKey = 'swirl-ai-provider-status';  // Used for Chat app status
  private promptsStatusKey = 'swirl-prompts-status';          // New key for Search app prompts
  private ragRatingStatusKey = 'swirl-rag-rating-status';
  private bulkOAuth2StatusKey = 'bulk-oauth2-status';
  private ConfidenceMixerStatusKey = 'swirl-confidence-mixer-status';
  private cookieConsentKey = 'swirl-cookie-consent';
  private supportFormKey = 'swirl-support-form';
  private swirlVersionKey = 'swirl-version';
  private readonly userKey = 'swirl-user';
  private readonly isSuperuserKey = 'swirl-is-superuser';
  private exportResultsKey = 'swirl-export-results';

  // Single source of truth for the username
  private currentUserSubject =
    new BehaviorSubject<string | null>(localStorage.getItem(this.userKey));

  // Observable that components can async‑pipe
  currentUser$ = this.currentUserSubject.asObservable();

  constructor(private http: HttpClient) {}

  login(username: string, password: string): any {
    const csrfToken = this.getCookie('csrftoken'); // get the CSRF token from the cookie
    const headers = new HttpHeaders().set('X-CSRFToken', csrfToken); // include the CSRF token in the request headers
    return this.http.post(`${this.apiUrl}/login/`, { username, password }, { headers }).pipe(
      tap((response: any) => {
        // Store token
        this.setToken(response.token);

        // Push the username into the service
        if (response.user) this.setUser(response.user);

        // Persist superuser flag and normalize to string 'true'/'false'
        if (typeof response.is_superuser !== 'undefined') {
          localStorage.setItem(this.isSuperuserKey, String(!!response.is_superuser));
        }

        // Store cookie consent flag
        if (response.cookie_consent) {
          this.setCookieConsent(response.cookie_consent === 'True');
        }

        // Store support form flag
        if (response.support_form) {
          this.setSupportForm(response.support_form === 'True');
        }

        // Store the SWIRL version
        if (response.swirl_version) {
        this.setSwirlVersion(response.swirl_version);
        }

        // Store Export Results feature flag (default false if missing)
        this.setExportResultsEnabled(!!response.export_results);
      })
    );
  }

  // Method to get cookie consent status
  getCookieConsent(): boolean {
    const consent = localStorage.getItem(this.cookieConsentKey);
    return consent === 'true'; // Ensure boolean conversion
  }

  // Method to set cookie consent status
  setCookieConsent(status: boolean): void {
    localStorage.setItem(this.cookieConsentKey, status.toString());
  }

  // Support form methods
  setSupportForm(hasSupportForm: boolean): void {
    localStorage.setItem(this.supportFormKey, hasSupportForm.toString());
  }

  getSupportForm(): boolean {
    return localStorage.getItem(this.supportFormKey) === 'true';
  }

  getSwirlVersion(): string | null {
    return localStorage.getItem(this.swirlVersionKey);
  }

  setSwirlVersion(version: string): void {
    localStorage.setItem(this.swirlVersionKey, version);
  }

  getIsSuperuser(): boolean {
    return localStorage.getItem(this.isSuperuserKey) === 'true';
  }

  oidcLogin(token: string, idp: string): any {
    const csrfToken = this.getCookie('csrftoken');
    const headers = new HttpHeaders()
      .set('X-CSRFToken', csrfToken)
      .set('OIDC-Token', `Bearer ${token}`);
  
    return this.http.post(`${this.apiUrl}/oidc_authenticate/?idp=${idp}`, {}, { headers }).pipe(
      tap((response: any) => {
        // Store token
        this.setToken(response.token);

        // Push the username into the service
        if (response.user) this.setUser(response.user);

        // Persist superuser flag and normalize to string 'true'/'false'
        if (typeof response.is_superuser !== 'undefined') {
          localStorage.setItem(this.isSuperuserKey, String(!!response.is_superuser));
        }

        // Store cookie consent flag
        if (response.cookie_consent) {
          this.setCookieConsent(response.cookie_consent === 'True');
        }
  
        // Store the SWIRL version
        if (response.swirl_version) {
          this.setSwirlVersion(response.swirl_version);
        }
        // Store Export Results feature flag (default false if missing)
        this.setExportResultsEnabled(!!response.export_results);
      })
    );
  }

  logout(): any {
    const csrfToken = this.getCookie('csrftoken');
    let headers = new HttpHeaders()
      .set('X-CSRFToken', csrfToken)
      .set('Authorization', 'Token ' + this.getToken())
      .set('Content-Type', 'application/json');
    return this.http.post(`${this.apiUrl}/swirl-logout/`, {}, { headers }).pipe(
      tap(() => this.clearUser())     // clear on success
    );
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  setToken(token: string): void {
    localStorage.setItem(this.tokenKey, token);
  }

  getChatStatus(): boolean {
    return localStorage.getItem(this.chatStatusKey) === 'true';
  }

  setChatStatus(status: boolean): void {
    localStorage.setItem(this.chatStatusKey, status.toString());
  }

  // --- AIProvider Status (for Chat) ---
  getAIProviderStatus(): boolean {
    return localStorage.getItem(this.AIProviderStatusKey) === 'true';
  }

  setAIProviderStatus(status: boolean): void {
    localStorage.setItem(this.AIProviderStatusKey, status.toString());
  }

  // --- Prompts Status (for Search) ---
  // Note: This now uses a separate local storage key.
  getPromptsStatus(): boolean {
    return localStorage.getItem(this.promptsStatusKey) === 'true';
  }

  setPromptsStatus(status: boolean): void {
    localStorage.setItem(this.promptsStatusKey, status.toString());
  }

  getRagRatingStatus(): boolean {
    return localStorage.getItem(this.ragRatingStatusKey) === 'true';
  }

  setRagRatingStatus(status: boolean): void {
    localStorage.setItem(this.ragRatingStatusKey, status.toString());
  }

  getBulkOAuth2Status(): boolean {
    return localStorage.getItem(this.bulkOAuth2StatusKey) === 'true';
  }

  setBulkOAuth2Status(status: boolean): void {
    localStorage.setItem(this.bulkOAuth2StatusKey, status.toString());
  }

  getConfidenceMixerStatus(): boolean {
    return localStorage.getItem(this.ConfidenceMixerStatusKey) === 'true';
  }

  setConfidenceMixerStatus(status: boolean): void {
    localStorage.setItem(this.ConfidenceMixerStatusKey, status.toString());
  }

  // Updated username helpers - get, set, and clear
  getUser(): string | null {
    return this.currentUserSubject.value;
  }

  setUser(user: string | null): void {
    if (user) {
      localStorage.setItem(this.userKey, user);
    } else {
      localStorage.removeItem(this.userKey);
    }
    this.currentUserSubject.next(user);
  }

  clearUser(): void {
    this.setUser(null);
    localStorage.removeItem(this.isSuperuserKey);
    localStorage.removeItem(this.exportResultsKey);
  }

  getCookie(name: string): string {
    let cookieValue = '';
    if (document.cookie && document.cookie !== '') {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.substring(0, name.length + 1) === (name + '=')) {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  }

  setCookie(name: string, value: string, minutes: number): void {
    let expires = '';
    if (minutes) {
      const date = new Date();
      date.setTime(date.getTime() + (minutes * 60 * 1000));
      expires = `; expires=${date.toUTCString()}`;
    }
    document.cookie = `${name}=${value}${expires}; path=/;`;
  }

  redirectToAuth(idp: string) {
    const token = this.getToken();
    if (token) {
      const path = window.location.pathname + window.location.search;
      this.setCookie('Token', token, 20);
      this.setCookie('Callback-Path', path, 20);
      window.location.href = `/api/swirl/${idp.toLowerCase()}-idp-auth`;
    }
  }

  isLoggedIn(): boolean {
    const token = this.getToken();
    return !!token;
  }

  bulkStartConnect(idps: string[]) {
    const token = this.getToken();
    if (token) {
      const returnTo = window.location.pathname + window.location.search;
      this.setCookie('Token', token, 20);

      const params = new URLSearchParams();
      params.set('idps', idps.join(','));
      params.set('returnTo', returnTo || '/');

      // Server will set callback path and chain redirects itself
      window.location.href = `/swirl/bulk/start-auth?${params.toString()}`;
    }
  }

  getExportResultsEnabled(): boolean {
    return localStorage.getItem(this.exportResultsKey) === 'true';
  }

  setExportResultsEnabled(status: boolean): void {
    localStorage.setItem(this.exportResultsKey, status.toString());
  }
}
